import { renderMathInDocument } from 'mathlive';
import { Delta } from 'quill/core';
import React, { useEffect, useImperativeHandle, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { ScrollView } from 'react-native';
import Column from 'src/components/10-atoms/layout/Column';
import Pressable from 'src/components/10-atoms/pressables/Pressable';
import useTheme from 'src/customizations/themes/hooks/useTheme';
import createThemedStyle from 'src/customizations/themes/utils/createThemedStyles';
import useCallbackWithCtx from 'src/hooks/useCallbackUtils/useCallbackWithCtx';
import useComponentId from 'src/hooks/useComponentId';
import useEffectWithCtx from 'src/hooks/useEffectUtils/useEffectWithCtx';
import styleUtils from 'src/utils/styles';
import { addMathFieldWrapper, normalizeMathField } from '../utils/mathField';
import useStyles from './useStyles';
import createQuillInstance from './utils/createQuillInstance';
const QuillEditor = React.forwardRef(({ initialValue, initialDelta, placeholder, readOnly = false, enableCachedImage = false, onEditorChanged, gutterBottom = 400, minEditorHeight = 100, typographyFontWeight, typographyVariant, ...columnProps }, ref) => {
    const [{ styles }] = useTheme(stylesGenerator, { minEditorHeight }, [
        minEditorHeight,
    ]);
    const containerRef = useRef(null);
    const quillRef = useRef(undefined);
    useEffect(() => {
        const container = containerRef.current;
        if (container) {
            const editorContainer = document.createElement('div');
            container.appendChild(editorContainer);
            const quill = createQuillInstance(editorContainer, {
                placeholder,
                readOnly,
                enableCachedImage,
            });
            quillRef.current = quill;
            if (initialDelta) {
                setDelta(new Delta(initialDelta));
            }
            else if (initialValue) {
                setHTML(initialValue);
            }
            onEditorChanged?.(quill);
            return () => {
                quillRef.current = null;
                onEditorChanged?.(null);
                container.innerHTML = '';
            };
        }
    }, []);
    useEffectWithCtx(() => {
        if (readOnly) {
            quillRef.current?.disable();
            renderMathInDocument();
        }
        else {
            quillRef.current?.enable();
        }
    }, {}, [readOnly]);
    const setDelta = useCallbackWithCtx((ctx, delta) => {
        if (quillRef.current) {
            quillRef.current.setContents(delta);
            renderMathInDocument();
        }
    }, { readOnly });
    const setHTML = useCallbackWithCtx((ctx, html) => {
        if (quillRef.current) {
            const delta = quillRef.current.clipboard.convert({
                html: ctx.readOnly
                    ? normalizeMathField(html)
                    : addMathFieldWrapper(html),
            });
            setDelta(delta);
        }
    }, { readOnly });
    useImperativeHandle(ref, () => ({
        setDelta,
        setHTML,
    }));
    const containerId = useComponentId();
    const editorStyles = useStyles(containerId, {
        typographyFontWeight,
        typographyVariant,
    });
    return (React.createElement(Column, { id: containerId, ...columnProps },
        React.createElement(Helmet, null,
            React.createElement("style", null, editorStyles)),
        readOnly ? (React.createElement(Column, { ref: containerRef, flex: true })) : (React.createElement(Pressable, { style: styleUtils.flex, touchFeedback: false, onPress: () => {
                quillRef.current?.getSelection(true);
            } },
            React.createElement(ScrollView, null,
                React.createElement(Column, { ref: containerRef, style: styles.editorContainer }),
                !!gutterBottom && React.createElement(Column, { style: { height: gutterBottom } }))))));
});
const stylesGenerator = createThemedStyle(({}, { minEditorHeight }) => ({
    editorContainer: {
        minHeight: minEditorHeight,
    },
}));
export default QuillEditor;
